import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost } from '../lib/api'
import type { DocumentDto, FolderDto, GroupDto, ProjectDto } from '../lib/types'

interface TrashBundle { groups: GroupDto[]; projects: ProjectDto[]; folders: FolderDto[]; documents: DocumentDto[] }

type Tab = 'groups' | 'projects' | 'folders' | 'documents'

export function Trash() {
  const qc = useQueryClient()
  const trash = useQuery({
    queryKey: ['trash'],
    queryFn: () => apiGet<TrashBundle>('/api/trash'),
  })
  const [tab, setTab] = useState<Tab>('groups')

  const restore = async (kind: Tab, uuid: string) => {
    const path: Record<Tab, string> = {
      groups: `/api/groups/${uuid}/unarchive`,
      projects: `/api/projects/${uuid}/unarchive`,
      folders: `/api/folders/${uuid}/unarchive`,
      documents: `/api/documents/${uuid}/unarchive`,
    }
    await apiPost(path[kind], {})
    qc.invalidateQueries({ queryKey: ['trash'] })
    qc.invalidateQueries({ queryKey: ['groups'] })
  }

  const tabs: { id: Tab; label: string; count: number }[] = [
    { id: 'groups', label: 'Groups', count: trash.data?.groups.length ?? 0 },
    { id: 'projects', label: 'Projects', count: trash.data?.projects.length ?? 0 },
    { id: 'folders', label: 'Folders', count: trash.data?.folders.length ?? 0 },
    { id: 'documents', label: 'Documents', count: trash.data?.documents.length ?? 0 },
  ]

  const items: { uuid: string; name: string }[] = trash.data
    ? (tab === 'groups'
        ? trash.data.groups.map((g) => ({ uuid: g.uuid, name: g.name }))
        : tab === 'projects'
          ? trash.data.projects.map((p) => ({ uuid: p.uuid, name: p.name }))
          : tab === 'folders'
            ? trash.data.folders.map((f) => ({ uuid: f.uuid, name: f.name }))
            : trash.data.documents.map((d) => ({ uuid: d.uuid, name: d.title })))
    : []

  return (
    <main className="content" style={{ padding: 24 }}>
      <h1>Trash</h1>
      <div style={{ display: 'flex', gap: 8, borderBottom: '1px solid var(--border-subtle)', marginBottom: 12 }}>
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`trash-tab${tab === t.id ? ' active' : ''}`}
            data-tab={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: '8px 16px', border: 'none', background: 'transparent',
              borderBottom: tab === t.id ? '2px solid var(--accent)' : '2px solid transparent',
              color: tab === t.id ? 'var(--text)' : 'var(--text-secondary)',
              cursor: 'pointer', fontWeight: tab === t.id ? 500 : 400,
            }}
          >{t.label} <span className="trash-count" data-count={t.id} data-value={t.count}>({t.count})</span></button>
        ))}
      </div>
      {items.length === 0 && trash.isSuccess && <p style={{ color: 'var(--text-secondary)' }}>Empty</p>}
      <ul id="trash-list" style={{ listStyle: 'none', padding: 0 }}>
        {items.map((it) => (
          <li key={it.uuid} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border-subtle)' }}>
            <span>{it.name}</span>
            <button className="btn btn-ghost btn-sm" onClick={() => restore(tab, it.uuid)}>Restore</button>
          </li>
        ))}
      </ul>
    </main>
  )
}
